import apiClient from '../../apis';
import type { Connection } from '../Connections/api';

/**
 * Agent Studio (#198): what agents may READ, granted and signed. Connectors
 * are the workspace's connections seen through their role; a grant is a
 * read scope on one of them; the audit log is the signed ledger of every
 * grant, revocation and deployment. Nothing here writes
 * through a connector — verb execution is deferred (#199).
 */

export type ConnectorRole = 'warehouse' | 'report' | 'timeline' | 'mcp' | 'knowledge' | 'email' | 'slack_member';

export interface Provenance {
  asserted_by: string;
  asserted_by_name: string;
  asserted_at: string;
  method?: string;
}

export interface Grant {
  id: string;
  connection_id: string;
  scope: 'read';
  provenance: Provenance;
  revoked_at: string | null;
  revoked_by: { by: string; name: string; at: string } | null;
  live: boolean;
  /**
   * The connection's credential reference or coordinates changed after the
   * grant was issued: the backend treats it as inert until an admin revokes
   * and grants again.
   */
  stale?: boolean;
  created_at: string;
}

export interface Connector {
  connection: Connection;
  role: ConnectorRole;
  grant: Grant | null;
  testable: boolean;
  /**
   * Only context feeds — report and timeline — take a grant. A warehouse is
   * reached through the seat holder's grounding (#200); mcp takes none.
   */
  grantable: boolean;
  freshness?: { last_sync_at: string | null; documents: number; claims: number; errors: string[] };
}

export type AuditKind = 'agent_deployed' | 'grant_issued' | 'grant_revoked' | 'voice_connected' | 'proposal_approved' | string;

export interface AuditRow {
  id: string;
  kind: AuditKind;
  subject: { seat_id?: string; agent_id?: string; connection_id?: string; grant_id?: string };
  summary: string;
  detail: Record<string, unknown>;
  provenance: Provenance;
  created_at: string;
}

/** Whether the caller may grant or revoke: an admin in an org, or anyone in a personal workspace. */
export interface StudioMe {
  can_manage_identity?: boolean;
  can_grant: boolean;
}

export function studioErrorMessage(e: any, fallback = 'Request failed'): string {
  return e?.response?.data?.error || e?.message || fallback;
}

export async function listConnectors(): Promise<Connector[]> {
  const { data } = await apiClient.get<Connector[]>('/api/agent-studio/connectors');
  return Array.isArray(data) ? data : [];
}

/** The only scope the API accepts is `read`; a write grant cannot be created through it. */
export async function grantConnector(connectionId: string): Promise<Grant> {
  const { data } = await apiClient.post<Grant>(`/api/agent-studio/connectors/${connectionId}/grant`, { scope: 'read' });
  return data;
}

export async function revokeConnector(connectionId: string): Promise<Grant> {
  const { data } = await apiClient.delete<Grant>(`/api/agent-studio/connectors/${connectionId}/grant`);
  return data;
}

export async function listAudit(limit = 100): Promise<AuditRow[]> {
  const { data } = await apiClient.get<AuditRow[]>('/api/agent-studio/audit', { params: { limit } });
  return Array.isArray(data) ? data : [];
}

export async function getStudioMe(): Promise<StudioMe> {
  const { data } = await apiClient.get<StudioMe>('/api/agent-studio/me');
  return { can_grant: !!data?.can_grant, can_manage_identity: !!data?.can_manage_identity };
}

export async function syncConnector(id: string): Promise<void> {
  await apiClient.post(`/api/knowledge/connections/${encodeURIComponent(id)}/sync`);
}


export async function provisionMailbox(seatId: string, connectionId: string, username: string): Promise<void> {
  await apiClient.post(`/api/agent-studio/seats/${encodeURIComponent(seatId)}/mailbox`, { connection_id: connectionId, username });
}

export async function deprovisionMailbox(seatId: string): Promise<void> {
  await apiClient.delete(`/api/agent-studio/seats/${encodeURIComponent(seatId)}/mailbox`);
}

export async function saveSeatIdentity(seatId: string, body: {
  identity: import('../Agents/api').SeatIdentity; slack_connection_id: string | null; mailbox_connection_id: string | null;
  sign_off: { terms_version: string; read_vendor_terms: true } | null;
}): Promise<void> {
  await apiClient.put(`/api/agent-studio/seats/${encodeURIComponent(seatId)}/identity`, body);
}

export interface RefreshBudget {
  budget_usd?: number | null;
  agent_refreshes_per_day: number;
  source_syncs_per_hour: number;
}

export interface RefreshRow {
  cost?: number;
  id: string;
  name: string;
  cadence: import('../Agents/api').Cadence;
  next_at: string | null;
  skipped: { at: string; reason: string } | null;
  can_edit: boolean;
  provider?: string;
  state?: string;
  last_run_at?: string | null;
  last_status?: string | null;
  last_sync_at?: string | null;
  freshness?: Connector['freshness'];
}

export interface RefreshTimeline {
  agents: RefreshRow[];
  sources: RefreshRow[];
  budget: RefreshBudget;
  usage: { agent_refreshes: number; source_syncs: number };
  cost: { usd: number; incomplete: boolean; today?: number; month?: number; budget_usd?: number | null };
  can_manage: boolean;
}

export async function getRefreshTimeline(): Promise<RefreshTimeline> {
  const { data } = await apiClient.get<RefreshTimeline>('/api/agent-studio/refresh');
  return data;
}

export async function saveRefreshBudget(budget: RefreshBudget): Promise<void> {
  await apiClient.put('/api/agent-studio/refresh-budget', budget);
}

export async function saveRefreshCadence(id: string, kind: 'agent' | 'source', cadence: import('../Agents/api').Cadence): Promise<void> {
  // #298 supplies the agent endpoint; a 404 until it lands is shown by the editor.
  const url = kind === 'agent' ? `/api/agents/${encodeURIComponent(id)}/refresh`
    : `/api/knowledge/connections/${encodeURIComponent(id)}/cadence`;
  await apiClient.put(url, cadence);
}

export interface AccessRow {
  id: string; at: string; reader_kind: 'person' | 'agent'; reader_id: string; on_behalf_of: string | null;
  document_id: string; document_title?: string; content_hash: string; connection_id: string; grant_id: string;
  purpose: string; session_id: string | null;
}
export interface AccessPage { rows: AccessRow[]; total: number; next: [string, string] | null }
export interface RetentionRow { id: string; name: string; retain_days: number }
export type AccessFilters = { since?: string; until?: string; reader?: string; connection?: string; document?: string };
export async function getAccessLog(params: AccessFilters & { cursor?: string }): Promise<AccessPage> {
  return (await apiClient.get<AccessPage>('/api/agent-studio/access', { params })).data;
}
export async function getRetention(): Promise<{ rows: RetentionRow[] }> {
  return (await apiClient.get<{ rows: RetentionRow[] }>('/api/agent-studio/retention')).data;
}
export async function saveRetention(id: string, retain_days: number): Promise<void> {
  await apiClient.put(`/api/agent-studio/connectors/${encodeURIComponent(id)}/retention`, { retain_days });
}
export function accessExportUrl(params: AccessFilters): string {
  return apiClient.getUri({ url: '/api/agent-studio/access/export', params });
}
export async function exportAccess(params: AccessFilters): Promise<void> {
  // Use the app's auth interceptor, then its existing Blob/anchor download pattern.
  const { data } = await apiClient.get<Blob>('/api/agent-studio/access/export', { params, responseType: 'blob' });
  const url = URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = 'knowledge-access.csv'; anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
